import { useEffect, useRef } from 'react'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'
import { checkHasLocalData, type ApiMapDataLike } from '../../../map-editor-shared/lib/graphJsonLocalData'

interface UseFloorSelectionProps {
  currentLot: string | null
  mapData: ApiMapDataLike | null
}

/**
 * Custom hook for managing floor selection and synchronization
 *
 * Responsibilities:
 * - Auto-selects first floor when entering a project
 * - Validates current floor belongs to current project
 * - Syncs API floors to FloorStore (creates floors from API data)
 * - Protects local data during floor switching (isFloorSwitching flag)
 */
export const useFloorSelection = ({ currentLot, mapData }: UseFloorSelectionProps): void => {
  const { currentFloor, floors, setCurrentFloor, updateFloor, updateFloorMapData, addFloor } = useFloorStore()
  const isFloorSwitching = useFloorStore((state) => state.isFloorSwitching)
  const prevLotRef = useRef<string | null>(null)

  // Select first floor if none selected (floor creation handled by FloorTabs)
  useEffect(() => {
    if (!currentLot) return

    // Use String() to ensure consistent type comparison
    const projectFloors = floors.filter(f => String(f.lotId) === String(currentLot))

    // CRITICAL FIX: If current floor doesn't belong to current project, reset it
    if (currentFloor) {
      const currentFloorData = floors.find(f => f.id === currentFloor)
      if (currentFloorData && String(currentFloorData.lotId) !== String(currentLot)) {
        setCurrentFloor(null)
        return
      }
    }

    // Only handle floor selection, NOT creation (FloorTabs handles creation)
    if (projectFloors.length > 0 && !currentFloor) {
      setCurrentFloor(projectFloors[0].id)
    }
  }, [currentLot, floors, currentFloor, setCurrentFloor])

  // Sync API floors to FloorStore (create floors if they don't exist)
  // IMPORTANT: Only sync on INITIAL load, not on every mapData change
  // This prevents overwriting local edits when server data is fetched
  useEffect(() => {
    if (!mapData || !currentLot) return

    // CRITICAL FIX: Skip server sync while floor is being switched
    // This prevents race condition where server response arrives after floor restoration
    // and overwrites the local data that was just restored
    if (isFloorSwitching) {
      return
    }

    // Detect project entry or switch to force server data sync
    // prevLotRef starts as null, so first entry (null → value) must also force sync
    const isProjectSwitch = prevLotRef.current !== currentLot
    prevLotRef.current = currentLot

    console.log(`[useFloorSelection] mapData arrived`, {
      floorCount: mapData.floors?.length ?? 0,
      isProjectSwitch,
      isFloorSwitching,
      existingFloorCount: useFloorStore.getState().floors.filter(f => String(f.lotId) === String(currentLot)).length,
    });

    // NOTE: A previous "stale floor preemptive delete on project switch" block
    // was removed here. It generated new local floor UUIDs on every re-entry,
    // which orphaned server-stored layer_type_mappings (saved with the OLD
    // UUIDs as floor_id). On re-entry loadMappings(NEW UUID) returned 0 records
    // → useEntityMappingRestore had nothing to apply → PropertyEditor showed
    // "No type assigned" for every CSV-imported object. cross-project
    // contamination is already prevented by the lotId filter above (line 29)
    // and by the per-floor sync-on-project-switch logic below
    // (`if (!hasLocalData || isProjectSwitch)`), so the delete-and-recreate
    // hack was unnecessary and behaviorally harmful. See
    // docs/03-analysis/restore-object-type-on-dashboard-reentry-v2.analysis.md.

    if (mapData.floors && mapData.floors.length > 0) {
      // Process each API floor
      mapData.floors.forEach((apiFloor, index) => {
        // CRITICAL FIX: Get freshest floor list from Zustand state on EACH iteration
        // This ensures we see floors created in previous iterations
        // Using hook's getFloorsByLotId would give stale data due to closure
        const freshFloors = useFloorStore.getState().floors
        const existingFloors = freshFloors.filter(f => String(f.lotId) === String(currentLot))

        // Check if floor already exists (by name and order)
        const existingFloor = existingFloors.find(
          f => f.name === apiFloor.name && f.order === (apiFloor.order || index)
        )

        console.log(`[useFloorSelection] floor match`, {
          apiName: apiFloor.name, apiOrder: apiFloor.order,
          matched: !!existingFloor,
          existingId: existingFloor?.id,
          hasApiGraphJson: !!apiFloor.graphJson,
          apiGraphJsonCells: (apiFloor.graphJson as any)?.cells?.length ?? 0,
        });

        if (!existingFloor && apiFloor.graphJson) {
          // Create new floor and then update with API data
          addFloor(String(currentLot))

          // Find the newly created floor and update it (get freshest state again)
          const latestFloors = useFloorStore.getState().floors.filter((f) => String(f.lotId) === String(currentLot))
          const newFloor = latestFloors[latestFloors.length - 1]

          if (newFloor) {
            updateFloor(newFloor.id, {
              name: apiFloor.name,
              order: apiFloor.order || index,
              mapData: {
                graphJson: apiFloor.graphJson,
                metadata: {},
                assets: [],
                objects: [],
                csv: null,
                dxf: null,
              }
            })
          }
        } else if (existingFloor && apiFloor.graphJson) {
          // Sync server data if:
          // 1. Local floor has no graphJson data (initial load), OR
          // 2. Project was just switched (force server data to prevent cross-project contamination)
          const hasLocalData = checkHasLocalData(existingFloor.mapData?.graphJson)

          if (!hasLocalData || isProjectSwitch) {
            updateFloorMapData(existingFloor.id, {
              graphJson: apiFloor.graphJson,
            })
          }
        }
      })
    }
  }, [mapData, currentLot, isFloorSwitching, updateFloorMapData, addFloor, updateFloor])
}
